import i18next from "i18next";

import { showAlert } from '../../../actions/globalActions';
import { ACCOUNT_ID_ROUTE_PLACEHOLDER, screenRoutes, TOKEN_ROUTE_PLACEHOLDER } from '../../../constants/routesPath';
import { LocalStorageService } from '../../../services/localStorageService';
import { buildRoutePath, generateUUID } from '../../../utils/commonUtil';
import { publicURL } from '../../../utils/config';

export const EDITED_MEDIA_ID_PREFIX = 'aeedit';
export namespace AdobeExpressUtils {
	export function timeout(ms: number) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
	export async function showLoadingMessagePromise(callback?: () => void) {
		await timeout(callback ? 1000 : 0);
		return callback?.();
	}
	export interface Size {
		width: number;
		height: number;
		unit: 'px' | 'in' | 'mm';
	}
	export interface FetchMediaDataProps {
		url: string;
		onDone: (_data: any) => void;
		setLoadingProgress?: (_value: number | false) => void;
		mediaDetails: {
			mediaName: string;
			mediaType: string;
		};
	}
	export interface AEProjectProps {
		ccEverywhere: any;
		actionCallback: any;
		projectId?: string; // required for edit design
		mediaName?: string;
		dataUrl?: string;
		replaceMediaContent?: object;
		mediaType?: string;
		canvasSize?: Size | string;
		catalogId?: string;
		setLoadingProgress?: (_value: number | false) => void;
		dispatch?: any;
		isFullScreenEditor?: boolean;
	}

	export const fullScreenEditorContainerConfig = {
		zIndex: 1,
		padding: 0,
		borderRadius: 0
	};
	const defaultContainerConfig = {
		zIndex: 1
	};
	const docConfig = { canvasSize: 'Video' };
	const baseAppConfig = {
		selectedCategory: 'media',
		allowedFileTypes: ['video/mp4', 'image/png', 'image/jpeg'],
	};
	const defaultExportConfig = {
		id: 'export-btn',
		label: 'Save & Export to Qbrick',
		action: {
			target: 'publish',
		},
		style: {
			uiType: 'button',
		},
	};
	const externalExportConfig = {
		id: 'export-btn',
		label: 'Save & Export',
		action: {
			target: 'publish',
		},
		style: {
			uiType: 'button',
		},
	};

	const accounts = ["Acc8mMQ36Cu7kCiDUOjgKWdeQ", "AccMmughOWgYEmUmXQ7xIhMKw"];
	const getExportConfig = () => {
		const currentAccount = LocalStorageService.getDefaultAccountId();
		if (currentAccount && accounts.includes(currentAccount)) {
			return [{ ...externalExportConfig, label: i18next.t("COMMON_AE_EXPORT_BUTTON") }];
		}
		return [{ ...defaultExportConfig, label: i18next.t("COMMON_AE_EXPORT_QBRICK_BUTTON") }];
	}

	export async function readJSON() {
		const publicDomain = publicURL.includes('localhost') ? "https://localhost:9000" : publicURL;
		const resp = fetch(`${publicDomain}/QbrickVideoEditor-196182-API-Key.json`);
		return await (await resp).json();
	}

	export async function initializeCcEverywhere() {
		try {
			const accountId = LocalStorageService.getDefaultAccountId() || "default";

			const credentialJSON = await readJSON();
			const credentialInfo = credentialJSON[accountId || 'default'] ?? credentialJSON["default"];

			const ccEverywhere = await (window as any).CCEverywhere.initialize({
				clientId: credentialInfo?.api_key?.client_id,
				appName: credentialInfo?.name,
			},
			{ locale: i18next.language || 'en', }
		);

			return ccEverywhere;
		} catch (error) {
			console.error('Error initializing ccEverywhere:', error);
			throw error;
		}
	}

	export function terminateCcEverywhere() {
		console.info('terminateCcEverywhere');
		// (window as any).CCEverywhere.terminate();
	}

	export async function fetchMediaDataWithProgress({
		url,
		onDone,
		setLoadingProgress,
		mediaDetails,
	}: FetchMediaDataProps) {
		fetch(url)
			.then((response) => {
				const contentLength = response.headers.get('content-length');
				if (!contentLength) {
					return response;
				}

				let loaded = 0;
				return new Response(
					new ReadableStream({
						start(controller) {
							const reader = response?.body?.getReader();
							readResponse();

							function readResponse() {
								if (!reader) {
									return;
								}

								reader.read().then((progressEvent) => {
									if (progressEvent.done) {
										controller.close();
										return;
									}
									loaded += progressEvent.value.byteLength;

									// Calculates % complete and updates progress text
									const percentageComplete = contentLength
										? Math.round((loaded / parseFloat(contentLength)) * 100)
										: 100;
									setLoadingProgress?.(percentageComplete);

									controller.enqueue(progressEvent.value);
									readResponse();
								});
							}
						},
					})
				);
			})
			.then((response) => response.blob())
			.then((mediaBlob) => {
				const uploadFile = new File([mediaBlob], mediaDetails.mediaName || 'Un-title Media', {
					type: mediaDetails.mediaType === 'video' ? 'video/mp4' : 'image/png',
				});
				showLoadingMessagePromise(() => setLoadingProgress?.(false)).then(() => {
					onDone(uploadFile);
				});
			});
	}

	export async function createNewProject({
		ccEverywhere,
		dataUrl,
		actionCallback,
		mediaName,
		mediaType = 'video',
		catalogId,
		setLoadingProgress,
		dispatch,
		isFullScreenEditor
	}: AEProjectProps) {
		try {
			const appConfig = {
				...baseAppConfig,
				allowedFileTypes: mediaType === 'video' ? baseAppConfig.allowedFileTypes : ['image/png', 'image/jpeg'],
				callbacks: {
					...actionCallback,
					onPublish: (intent: any, publishParams: any) => {
						actionCallback.onPublish(intent, publishParams, mediaName, undefined, catalogId);
					},
					onCancel: () => {
						actionCallback.onCancel();
						isFullScreenEditor && window.close();
					},
					onError: (_error: any) => {
						if (dispatch) {
							dispatch(showAlert(i18next.t("ADOBE_EXPRESS_NO_PERMISSION")));
						}
						setTimeout(() => {
							isFullScreenEditor && window.close();
						}, 1000);
					}
				},
			};

			if (!ccEverywhere) {
				isFullScreenEditor && window.close();
				setLoadingProgress?.(false);
				return;
			}

			const exportConfig = getExportConfig();
			if (dataUrl) {
				fetchMediaDataWithProgress({
					url: dataUrl,
					onDone: (uploadFile) => {
						ccEverywhere.editor.createWithAsset(
							{
								...docConfig,
								asset: {
									type: mediaType,
									dataType: 'blob',
									data: uploadFile,
								},
							},
							appConfig,
							exportConfig,
							isFullScreenEditor ? fullScreenEditorContainerConfig : defaultContainerConfig
						);
					},
					setLoadingProgress,
					mediaDetails: { mediaName: mediaName || 'Un-title Media', mediaType: mediaType },
				});
			} else {
				showLoadingMessagePromise(() => setLoadingProgress?.(false)).then(() => {
					ccEverywhere.editor.create(docConfig, appConfig, exportConfig, isFullScreenEditor ? fullScreenEditorContainerConfig : defaultContainerConfig);
				});
			}
		} catch (error) {
			console.error('Error creating design:', error);
			isFullScreenEditor && window.close();
			setLoadingProgress?.(false);
		}
	}

	export async function editDesign({
		ccEverywhere,
		projectId,
		actionCallback,
		mediaName,
		replaceMediaContent,
		catalogId,
		setLoadingProgress,
		dispatch,
		isFullScreenEditor
	}: AEProjectProps) {
		const exportConfig = getExportConfig();
		const editDocConfig = { documentId: projectId };
		const appConfig = {
			...baseAppConfig,
			callbacks: {
				...actionCallback,
				onPublish: (intent: any, publishParams: any) => {
					actionCallback.onPublish(intent, publishParams, mediaName, replaceMediaContent, catalogId);
				},
				onCancel: () => {
					actionCallback.onCancel();
					isFullScreenEditor && window.close();
				},
				onError: (_error: any) => {
					if (dispatch) {
						dispatch(showAlert(i18next.t("ADOBE_EXPRESS_NO_PERMISSION")));
					}
					setTimeout(() => {
						isFullScreenEditor && window.close();
					}, 1000);
				},
			},
		};
		showLoadingMessagePromise(() => setLoadingProgress?.(false)).then(() => {
			ccEverywhere && ccEverywhere.editor.edit(editDocConfig, appConfig, exportConfig, isFullScreenEditor ? fullScreenEditorContainerConfig : defaultContainerConfig);
		});
	}

	export async function trimVideo({
		ccEverywhere,
		dataUrl,
		actionCallback,
		mediaName,
		replaceMediaContent,
		catalogId,
		setLoadingProgress,
		dispatch,
		isFullScreenEditor
	}: AEProjectProps) {
		try {
			const appConfig = {
				callbacks: {
					...actionCallback,
					onPublish: (intent: any, publishParams: any) => {
						actionCallback.onPublish(
							intent,
							{ ...publishParams, projectId: generateUUID() },
							mediaName,
							replaceMediaContent,
							catalogId
						);
					},
					onCancel: () => {
						actionCallback.onCancel();
						isFullScreenEditor && window.close();
					},
					onError: (_error: any) => {
						if (dispatch) {
							dispatch(showAlert(i18next.t("ADOBE_EXPRESS_NO_PERMISSION")));
						}
						setTimeout(() => {
							isFullScreenEditor && window.close();
						}, 1000);
					},
				},
			};

			if (dataUrl) {
				fetchMediaDataWithProgress({
					url: dataUrl,
					onDone: (uploadFile) => {
						ccEverywhere.quickAction.trimVideo(
							{
								asset: {
									type: "video",
									dataType: 'blob',
									data: uploadFile,
								},
							},
							appConfig,
							getExportConfig(),
							defaultContainerConfig
						);
					},
					setLoadingProgress,
					mediaDetails: { mediaName: mediaName || 'Un-title Media', mediaType: "video" },
				});
			} else {
				console.error('Error trim video: No dataUrl provided');
				isFullScreenEditor && window.close();
				setLoadingProgress?.(false);
			}
		} catch (error) {
			console.error('Error trim video:', error);
			isFullScreenEditor && window.close();
			setLoadingProgress?.(false);
		}
	}

	export async function resizeVideo({
		ccEverywhere,
		dataUrl,
		actionCallback,
		mediaName,
		replaceMediaContent,
		catalogId,
		setLoadingProgress,
		dispatch,
		isFullScreenEditor
	}: AEProjectProps) {
		try {
			const appConfig = {
				callbacks: {
					...actionCallback,
					onPublish: (intent: any, publishParams: any) => {
						actionCallback.onPublish(
							intent,
							{ ...publishParams, projectId: generateUUID() },
							mediaName,
							replaceMediaContent,
							catalogId
						);
					},
					onCancel: () => {
						actionCallback.onCancel();
						isFullScreenEditor && window.close();
					},
					onError: (_error: any) => {
						if (dispatch) {
							dispatch(showAlert(i18next.t("ADOBE_EXPRESS_NO_PERMISSION")));
						}
						setTimeout(() => {
							isFullScreenEditor && window.close();
						}, 1000);
					},
				},
			};
			if (dataUrl) {
				fetchMediaDataWithProgress({
					url: dataUrl,
					onDone: (uploadFile) => {
						ccEverywhere.quickAction.resizeVideo(
							{
								asset: {
									type: "video",
									dataType: 'blob',
									data: uploadFile,
								},
							},
							appConfig,
							getExportConfig(),
							defaultContainerConfig
						);
					},
					setLoadingProgress,
					mediaDetails: { mediaName: mediaName || 'Un-title Media', mediaType: "video" },
				});
			} else {
				console.error('Error resize video: No dataUrl provided');
				isFullScreenEditor && window.close();
				setLoadingProgress?.(false);
			}
		} catch (error) {
			console.error('Error resize video:', error);
			isFullScreenEditor && window.close();
			setLoadingProgress?.(false);
		}
	}

	export async function resizeImage({
		ccEverywhere,
		dataUrl,
		actionCallback,
		mediaName,
		replaceMediaContent,
		catalogId,
		setLoadingProgress,
		dispatch,
		isFullScreenEditor
	}: AEProjectProps) {
		const editDocConfig = {
			asset: {
				type: 'image',
				dataType: 'url',
				data: dataUrl,
			},
		};
		const appConfig = {
			callbacks: {
				...actionCallback,
				onPublish: (intent: any, publishParams: any) => {
					actionCallback.onPublish(
						intent,
						{ ...publishParams, projectId: generateUUID() },
						mediaName,
						replaceMediaContent,
						catalogId
					);
				},
				onCancel: () => {
					actionCallback.onCancel();
					isFullScreenEditor && window.close();
				},
				onError: (_error: any) => {
					if (dispatch) {
						dispatch(showAlert(i18next.t("ADOBE_EXPRESS_NO_PERMISSION")));
					}
					setTimeout(() => {
						isFullScreenEditor && window.close();
					}, 1000);
				},
			},
		};
		showLoadingMessagePromise(() => setLoadingProgress?.(false)).then(() => {
			ccEverywhere.quickAction.resizeImage(editDocConfig, appConfig, getExportConfig(), defaultContainerConfig);
		});
	}

	export const getMediaIdFromProjectId = (projectId: string) => {
		return `${EDITED_MEDIA_ID_PREFIX}-${new Date().getTime()}-${projectId.replaceAll(':', '-')}`;
	};

	export const getAdobeExpressProjectIdFromMediaId = (mediaId: string) => {
		const [, , a, b, c, d] = (mediaId ?? '').split('-');
		const projectUUID = mediaId.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0];

		if (!a || !b || !c || !d || !projectUUID) {
			return null;
		}

		return `${a}:${b}:${c}:${d}:${projectUUID}`;
	};

	export const redirectToAdobeExpressOnlyPage = ({ projectId, dataUrl, replaceMedia, accountId, mediaData, isImage, action, updateToAssetId }
		: {
			accountId: string;
			action: string;
			projectId?: string;
			dataUrl?: string;
			isImage?: boolean;
			updateToAssetId?: string;
			replaceMedia?: { mediaId?: string; assetId?: string; };
			mediaData?: { mediaName?: string; catalogId?: string; };
		}) => {
		const token = LocalStorageService.getAccessToken();
		const windowWidth = window.innerWidth > 0 ? window.innerWidth : window.screen.width;
		const windowHeight = window.innerHeight > 0 ? window.innerHeight : window.screen.height;
		const newWindowParams = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=${windowWidth},height=${windowHeight},top=100,rel=noreferrer`;
		const baseUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/#`;

		const { mediaId: replaceMediaId, assetId: replaceMediaAssetId } = replaceMedia ?? {};
		const { mediaName, catalogId } = mediaData ?? {};
		const path = `${screenRoutes.ADOBE_EXPRESS}${`?action=${action}`}${isImage ? `&isImage=true` : ''}
			${projectId ? `&projectId=${projectId}` : ''}${dataUrl ? `&dataUrl=${encodeURIComponent(dataUrl)}` : ''}
			${replaceMediaId ? `&replaceMediaId=${replaceMediaId}` : ''}${replaceMediaAssetId ? `&replaceMediaAssetId=${replaceMediaAssetId}` : ''}
			${mediaName ? `&mediaName=${encodeURIComponent(mediaName)}` : ''}${catalogId ? `&catalogId=${catalogId}` : ''}
			${updateToAssetId ? `&updateToAssetId=${updateToAssetId}` : ''}`;
		const editorURL = baseUrl + buildRoutePath(path, ACCOUNT_ID_ROUTE_PLACEHOLDER, accountId);

		let ssoLoginUrl = `${baseUrl}/sso-login/${TOKEN_ROUTE_PLACEHOLDER}/${ACCOUNT_ID_ROUTE_PLACEHOLDER}`;
		ssoLoginUrl = buildRoutePath(ssoLoginUrl, ACCOUNT_ID_ROUTE_PLACEHOLDER, accountId);
		ssoLoginUrl = buildRoutePath(ssoLoginUrl, TOKEN_ROUTE_PLACEHOLDER, token);
		ssoLoginUrl = `${ssoLoginUrl}?redirectRoute=${encodeURIComponent(editorURL)}&lang=${i18next.language}`;

		window.open(ssoLoginUrl, '_blank', newWindowParams);
	};
}
